import React, { useContext, useEffect, useState } from "react";
import API from "../../api/api";
import Navbar from "../../components/Navbar";
import Swal from "sweetalert2";
import SearchableSelect from "../../components/common/SearchableSelect";
import { AuthContext } from "../../context/AuthContext";

const PlayerClassification = () => {
  const { role } = useContext(AuthContext);
  const isDistrictUser = String(role || "").toLowerCase() === "district";
  const [players, setPlayers] = useState([]);
  const [classifiedPlayers, setClassifiedPlayers] = useState([]);
  const [sports, setSports] = useState([]);
  const [classifications, setClassifications] = useState([]);
  const [selectedSport, setSelectedSport] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [selectedClass, setSelectedClass] = useState("");
  const [filterPlayer, setFilterPlayer] = useState("");
  const [filterSport, setFilterSport] = useState("");
  const [filterClass, setFilterClass] = useState("");

  useEffect(() => {
    loadPlayers();
    loadClassifiedPlayers();
    loadSports();
  }, []);

  const loadPlayers = async () => {
    try {
      const res = await API.get("/admin/classifications/players");
      setPlayers(Array.isArray(res) ? res : res.data || []);
    } catch (err) {
      console.error("Error loading players:", err);
      setPlayers([]); // 💡 important
    }
  };

  const filteredClassifiedPlayers = classifiedPlayers.filter((p) => {
    return (
      (!filterPlayer || p.player_name.toLowerCase().includes(filterPlayer.toLowerCase())) &&
      (!filterSport || p.sport_name === filterSport) &&
      (!filterClass || p.classification === filterClass)
    );
  });

  const handleRevoke = async (row) => {
    const result = await Swal.fire({
      title: "Revoke Classification?",
      html: `
        <p><strong>Player:</strong> ${row.player_name}</p>
        <p><strong>Sport:</strong> ${row.sport_name}</p>
        <p><strong>Classification:</strong> ${row.classification}</p>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, revoke",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#d33",
    });

    if (!result.isConfirmed) return;

    try {
      await API.delete(
        `/admin/classifications/revoke/${row.player_category_id}`
      );

      await Swal.fire({
        icon: "success",
        title: "Revoked!",
        timer: 2000,
        showConfirmButton: false,
      });

      loadClassifiedPlayers();
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Failed",
        text: err.response?.data?.message || "Could not revoke classification",
      });
    }
  };

  const loadClassifiedPlayers = async () => {
    try {
      const res = await API.get("/admin/classifications/classified-players");
      setClassifiedPlayers(Array.isArray(res) ? res : res.data || []);
    } catch (err) {
      console.error("Error loading classified players:", err);
      setClassifiedPlayers([]); // 💡 important
    }
  };

  const loadSports = async () => {
    try {
      const res = await API.get("/sports");
      setSports(Array.isArray(res) ? res : res.data || []);
    } catch (err) {
      console.error("Error loading sports:", err);
      setSports([]); // 💡 important
    }
  };

  const loadClassifications = async (sportId) => {
    try {
      const res = await API.get(
        `/admin/classifications/sports/${sportId}/classifications`
      );
      setClassifications(Array.isArray(res) ? res : res.data || []);
    } catch (err) {
      console.error("Error loading classifications:", err);
      setClassifications([]);
    }
  };

  const handleAssign = async () => {
    if (!selectedPlayer || !selectedSport || !selectedClass) {
      await Swal.fire({
        icon: "warning",
        title: "Missing selection",
        text: "Please select player, sport, and classification.",
      });
      return;
    }

    const sportName =
      sports.find((s) => String(s.id) === String(selectedSport))?.name ||
      "selected sport";
    const newClassName =
      classifications.find((c) => String(c.id) === String(selectedClass))
        ?.classification || selectedClass;

    try {
      // FIRST: Check if already exists (dry run)
      const checkResponse = await API.post("/admin/classifications/assign", {
        player_id: selectedPlayer.player_id,
        sport_id: selectedSport,
        classification_id: selectedClass,
      });

      // Show appropriate confirmation dialog
      if (checkResponse.action === "replaced") {
        // Show REPLACE confirmation with existing details
        const result = await Swal.fire({
          title: "Replace Existing Classification?",
          html: `
            <div style="text-align: left;">
              <p><strong>Player:</strong> ${selectedPlayer.player_name}</p>
              <p><strong>Sport:</strong> ${checkResponse.sport_name}</p>
              <hr style="margin: 10px 0;">
              <p><strong>Current:</strong> "${checkResponse.existing_classification}"</p>
              <p><strong>New:</strong> "${newClassName}"</p>
              <hr style="margin: 10px 0;">
              <p>Replace with new classification?</p>
            </div>
          `,
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Yes, replace",
          cancelButtonText: "Keep current",
          confirmButtonColor: "#3085d6",
          cancelButtonColor: "#d33",
        });

        if (!result.isConfirmed) {
          return;
        }
      } else {
        // Show NEW assignment confirmation
        const result = await Swal.fire({
          title: "Assign New Classification?",
          html: `
            <div style="text-align: left;">
              <p><strong>Player:</strong> ${selectedPlayer.player_name}</p>
              <p><strong>Sport:</strong> ${sportName}</p>
              <p><strong>Classification:</strong> "${newClassName}"</p>
            </div>
          `,
          icon: "question",
          showCancelButton: true,
          confirmButtonText: "Yes, assign",
          cancelButtonText: "Cancel",
          confirmButtonColor: "#3085d6",
        });

        if (!result.isConfirmed) {
          return;
        }
      }

      // SUCCESS: Data was already saved by the check call!
      await Swal.fire({
        icon: "success",
        title: checkResponse.action === "replaced" ? "Replaced!" : "Assigned!",
        text: checkResponse.action === "replaced" 
          ? `Changed from "${checkResponse.existing_classification}" to "${newClassName}"`
          : `Assigned "${newClassName}" successfully`,
        timer: 2500,
        showConfirmButton: false,
      });

      // REFRESH DATA
      await Promise.all([
        loadPlayers(),
        loadClassifiedPlayers()
      ]);
      setSelectedPlayer(null);
      setSelectedSport("");
      setSelectedClass("");
    } catch (err) {
      console.error("Error:", err);
      Swal.fire({
        icon: "error",
        title: "Error!",
        text: err.response?.data?.message || "Failed to save classification.",
      });
    }
  };

  return (
    <>
      <Navbar />
      <div className="max-w-7xl mx-auto p-3 sm:p-4 md:p-6 mobile-page-pad">
        <section className="mb-6 rounded-2xl border border-amber-200 bg-gradient-to-br from-amber-50 via-white to-sky-50 p-4 sm:p-5 shadow-sm">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-sky-700">Classification Office</p>
              <h1 className="text-2xl sm:text-3xl font-semibold text-slate-900 mt-1">Classification Allotment</h1>
              <p className="text-sm text-slate-600 mt-1">
                Assign sport-specific classes and keep allotment records auditable.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Players</p>
                <p className="text-lg font-semibold text-slate-900">{players.length}</p>
              </div>
              <div className="rounded-xl border border-slate-200 bg-white px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-slate-500">Sports</p>
                <p className="text-lg font-semibold text-slate-900">{sports.length}</p>
              </div>
              <div className="rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-emerald-700">Classified</p>
                <p className="text-lg font-semibold text-emerald-800">{classifiedPlayers.length}</p>
              </div>
              <div className="rounded-xl border border-cyan-200 bg-cyan-50 px-3 py-2">
                <p className="text-[11px] uppercase tracking-wide text-cyan-700">Filtered</p>
                <p className="text-lg font-semibold text-cyan-800">{filteredClassifiedPlayers.length}</p>
              </div>
            </div>
          </div>
        </section>

        {/* Selection Panel */}
        <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_280px] mb-6">
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-md">
          <h2 className="font-semibold text-lg mb-4 text-gray-700">Assign Classification</h2>

          <div className="grid md:grid-cols-3 gap-4">
            <SearchableSelect
              onChange={(e) => {
                const player = players.find((p) => p.player_id === Number(e.target.value));
                setSelectedPlayer(player);
              }}
              value={selectedPlayer?.player_id || ""}
              className="border p-2 rounded w-full"
            >
              <option value="">Select Player</option>
              {players?.map((p) => (
                <option key={p.player_id} value={p.player_id}>
                  {p.player_name}
                </option>
              ))}
            </SearchableSelect>

            <SearchableSelect
              onChange={(e) => {
                setSelectedSport(e.target.value);
                if (e.target.value) {
                  loadClassifications(e.target.value);
                } else {
                  setClassifications([]);
                  setSelectedClass("");
                }
              }}
              value={selectedSport}
              className="border p-2 rounded w-full"
            >
              <option value="">Select Sport</option>
              {sports?.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name}
                </option>
              ))}
            </SearchableSelect>

            <SearchableSelect
              onChange={(e) => setSelectedClass(e.target.value)}
              value={selectedClass}
              className="border p-2 rounded w-full"
              disabled={!selectedSport}
            >
              <option value="">Select Classification</option>
              {classifications?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.classification}
                </option>
              ))}
            </SearchableSelect>
          </div>

          <div className="flex justify-end mt-4">
            <button
              onClick={handleAssign}
              disabled={!selectedPlayer || !selectedSport || !selectedClass}
              className="bg-blue-600 text-white px-6 py-2 rounded hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition"
            >
              Save Classification
            </button>
          </div>
        </div>
        <aside className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <p className="text-sm font-semibold text-slate-900">Assignment Quality Checks</p>
            <ul className="mt-2 space-y-1.5 text-xs text-slate-600">
              <li>Verify player identity before class assignment.</li>
              <li>Match the sport-specific classification set.</li>
              <li>Existing allotments can be changed only by the state.</li>
              <li>District users may add a missing classification only.</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4">
            <p className="text-xs font-semibold uppercase tracking-[0.12em] text-amber-800">Tip</p>
            <p className="mt-1 text-sm text-amber-900">
              {isDistrictUser
                ? "Once allotted, a classification is locked for district users."
                : "Filter by sport and class before classification reviews."}
            </p>
          </div>
        </aside>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-4">
        <div className="grid md:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Filter by Player Name"
            value={filterPlayer}
            onChange={(e) => setFilterPlayer(e.target.value)}
            className="border p-2 rounded"
          />

          <SearchableSelect
            value={filterSport}
            onChange={(e) => setFilterSport(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="">All Sports</option>
            {[...new Set(classifiedPlayers.map(p => p.sport_name))].map(s => (
              <option key={s} value={s}>{s}</option>
            ))}
          </SearchableSelect>

          <SearchableSelect
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            className="border p-2 rounded"
          >
            <option value="">All Classifications</option>
            {[...new Set(classifiedPlayers.map(p => p.classification))].map(c => (
              <option key={c} value={c}>{c}</option>
            ))}
          </SearchableSelect>
        </div>
        </div>

        {/* Classified Players Table */}
        <section className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
          <div className="border-b border-slate-200 bg-slate-50/70 px-4 py-3">
            <h3 className="text-xl font-semibold text-gray-800">
              Classified Players ({classifiedPlayers.length})
            </h3>
            <p className="text-sm text-slate-600">
              {isDistrictUser
                ? "Review current assignments. All allotted classifications are state-locked."
                : "Review current assignments and revoke when needed."}
            </p>
          </div>
        <div className="overflow-x-auto p-0">
          <table className="min-w-full border border-gray-300 rounded-lg">
            <thead className="bg-blue-100 text-gray-700">
              <tr>
                <th className="p-3 border">Player</th>
                <th className="p-3 border">Gender</th>
                <th className="p-3 border">Sport</th>
                <th className="p-3 border">Classification</th>
                <th className="p-3 border">Action</th>
              </tr>
            </thead>
            <tbody>
              {filteredClassifiedPlayers.map((p) => (
                <tr key={`${p.player_id}-${p.sport_id}`} className="text-center">
                  <td className="p-3 border">{p.player_name}</td>
                  <td className="p-3 border">{p.gender}</td>
                  <td className="p-3 border">{p.sport_name}</td>
                  <td className="p-3 border">{p.classification}</td>
                  <td className="p-3 border">
                    {isDistrictUser ? (
                      <span className="inline-flex rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                        State locked
                      </span>
                    ) : (
                      <button
                        onClick={() => handleRevoke(p)}
                        className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700"
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
              {classifiedPlayers.length === 0 && (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-gray-500">
                    No players classified yet. Assign classifications above.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        </section>
      </div>
    </>
  );
};

export default PlayerClassification;
